'use client';

import React, { useState } from 'react';
import { usePathname } from 'next/navigation';
import { socials } from '@/types/socials';
import { motion, AnimatePresence } from 'framer-motion';
import { FaShareAlt } from 'react-icons/fa';
import { MessageSquare, CloudRain } from 'lucide-react';
import ChatSidebar from '@/app/components/Chat/ChatSidebar';
import { useRain } from '@/app/hooks/context/RainContext';

export default function RightBar() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // The join window is only two minutes long and the chat is a drawer, so the
  // button itself has to announce it — otherwise anyone browsing with the panel
  // closed misses the rain entirely.
  const { enabled, rain, secondsLeft } = useRain();
  const rainOpen = enabled && rain?.status === 'open' && secondsLeft > 0;
  const rainJoined = rainOpen && rain?.joined;

  if (pathname?.startsWith('/raffle-overlay')) return null;

  return (
    <>
      <ChatSidebar open={chatOpen} onClose={() => setChatOpen(false)} />

      {/* Hidden while a slot game is open — body.slot-open is toggled by the slots page (see globals.css) */}
      <div className="floating-right-bar fixed right-4 bottom-4 z-50 flex flex-col items-center gap-3">
        {/* Chat button — always visible, above socials */}
        <button
          onClick={() => setChatOpen(!chatOpen)}
          title={rainOpen ? (rainJoined ? "You're in the rain — open chat" : 'Rain is open — join in chat') : 'Open chat'}
          className={`relative flex items-center justify-center w-12 h-12 rounded-full border shadow-lg transition-all duration-200 ${
            chatOpen
              ? 'bg-green-400 text-black border-green-400 hover:bg-green-300 hover:shadow-emerald-500/30'
              : 'bg-black text-green-400 border-green-500/40 hover:bg-green-400/10 hover:border-green-400/70 hover:shadow-emerald-500/20'
          }`}
        >
          <MessageSquare className="w-6 h-6" />

          {rainOpen && (
            <>
              {/* Pulsing ring — reads as "something is happening" from across the page */}
              {!rainJoined && (
                <motion.span
                  aria-hidden
                  className="absolute inset-0 rounded-full border-2 border-green-400"
                  animate={{ scale: [1, 1.35], opacity: [0.7, 0] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'easeOut' }}
                />
              )}
              <span
                className={`absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 rounded-full shadow ${
                  rainJoined ? 'bg-emerald-400 text-black ring-2 ring-black' : 'bg-green-400 text-black ring-2 ring-black'
                }`}
              >
                <CloudRain className="w-3 h-3" />
              </span>
            </>
          )}
        </button>

        {/* Social buttons appear above the share bubble */}
        <AnimatePresence>
          {open &&
            socials
              .slice()
              .reverse()
              .map((social, i) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={i}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ delay: i * 0.05 }}
                    className={`flex items-center justify-center w-12 h-12 rounded-full text-green-400 border border-green-500/40 bg-black shadow-lg transition-all duration-200 ${social.color} hover:bg-green-400/10 hover:border-green-400/70 hover:shadow-emerald-500/20`}
                  >
                    <Icon className="w-6 h-6" />
                  </motion.a>
                );
              })}
        </AnimatePresence>

        {/* Share bubble at bottom */}
        <button
          onClick={() => setOpen(!open)}
          className={`flex items-center justify-center w-12 h-12 rounded-full border shadow-lg transition-all duration-200 ${
            open
              ? 'bg-green-400 text-black border-green-400 hover:bg-green-300 hover:shadow-emerald-500/30'
              : 'bg-black text-green-400 border-green-500/40 hover:bg-green-400/10 hover:border-green-400/70 hover:shadow-emerald-500/20'
          }`}
        >
          <FaShareAlt className="w-6 h-6" />
        </button>
      </div>
    </>
  );
}
